<script lang="ts">
/**
 * What the cell is showing. `blank` renders the em-dash; `pending`/`running`
 * add a spinner and italicise the caller's wording; `failed` swaps in the
 * error badge. Views pick a member through `contractCellStateMap` rather than
 * reading a task status here.
 */
export type GaiaGridCellState = 'value' | 'blank' | 'pending' | 'running' | 'failed';
</script>

<script setup lang="ts">
import { computed, type PropType } from 'vue';
import GaiaText from '../../base/GaiaText.vue';
import GaiaOverwriteBadge from '../extraction/GaiaOverwriteBadge.vue';
import { gaiaAccent, gaiaFontWeight, gaiaNeutral, gaiaStatus } from '../../themes/tokens';

const props = defineProps({
  /** The cell's text. Use the default slot for anything richer (chips, links). */
  text: {
    type: String,
    default: undefined,
  },
  state: {
    type: String as PropType<GaiaGridCellState>,
    default: 'value',
  },
  /** Column width — every cell of a column is sized from the same number. */
  width: {
    type: [Number, String],
    default: 200,
  },
  /** The frozen name column. */
  sticky: {
    type: Boolean,
    default: false,
  },
  stickyLeft: {
    type: [Number, String],
    default: 44,
  },
  /** This cell owns the selection ring and the open context menu. */
  selected: {
    type: Boolean,
    default: false,
  },
  /** A file row nested under its contract — tints the whole row. */
  child: {
    type: Boolean,
    default: false,
  },
  /** Draws the tree chevron (contract rows in the name column). */
  expandable: {
    type: Boolean,
    default: false,
  },
  expanded: {
    type: Boolean,
    default: false,
  },
  /** Draws the file glyph that indents a child row's name. */
  fileIndent: {
    type: Boolean,
    default: false,
  },
  /** A human replaced the extracted value — trailing violet marker. */
  overwritten: {
    type: Boolean,
    default: false,
  },
  /** Tooltip on that marker, e.g. "Manually overwritten by A. Rossi". */
  overwriteTitle: {
    type: String,
    default: undefined,
  },
  /** Tooltip on the failed badge — the extraction error. */
  errorTitle: {
    type: String,
    default: undefined,
  },
  /** A human signed off on the value — the green corner check. */
  approved: {
    type: Boolean,
    default: false,
  },
  /**
   * Drop the cell's own frame — width, background, right border and padding —
   * for a mount that already supplies one. The insight grid renders these inside
   * `n-data-table`'s own `<td>`, which owns the column width and the grid lines;
   * without this the two frames double up.
   */
  frameless: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits<{
  select: [event: MouseEvent];
  toggle: [event: MouseEvent];
}>();

const size = (value: number | string): string => (typeof value === 'number' ? `${value}px` : value);

const cellWidth = computed(() => size(props.width));
const left = computed(() => (props.sticky ? size(props.stickyLeft) : 'auto'));
const position = computed(() => (props.sticky ? 'sticky' : 'relative'));
// The frozen column has to paint over the scrolling ones; a selected cell has
// to paint over its neighbours so its ring isn't clipped by them.
const layer = computed(() => (props.sticky ? 3 : props.selected ? 2 : 0));

const busy = computed(() => props.state === 'pending' || props.state === 'running');
const spinnerColor = computed(() =>
  props.state === 'running' ? gaiaAccent.base : gaiaNeutral.textMuted
);

const textTone = computed(() => {
  if (props.state === 'blank') return 'blank';
  if (props.state === 'failed') return 'alert';
  return busy.value ? 'faint' : 'ink';
});

const background = computed(() => {
  if (props.selected) return gaiaAccent.surface;
  return props.child ? gaiaNeutral.surfaceChild : gaiaNeutral.surface;
});
// An inset ring rather than a border: a real border would shift the 1px grid.
const ring = computed(() => (props.selected ? `inset 0 0 0 1.5px ${gaiaNeutral.ink}` : 'none'));
const chevronRotation = computed(() => (props.expanded ? '90deg' : '0deg'));

const border = gaiaNeutral.borderSoft;
const hover = gaiaNeutral.surfaceHover;
const chevron = gaiaNeutral.textSubtle;
const ink = gaiaNeutral.ink;
const blank = gaiaNeutral.textBlank;
const alert = gaiaStatus.alert;
const alertTint = gaiaStatus.alertTint;
const success = gaiaStatus.success;
// The badge's weight is a scale step; its 9.5px is not — see the rule below.
const bold = String(gaiaFontWeight.bold);
</script>

<template>
  <!-- One cell of the contract-view spreadsheet. It carries every state the
       grid can be in — tree expander, file indent, extraction spinner, failure
       badge, overwrite marker, selection ring — because they compose inside a
       single fixed-width box that naive's table cell can't reproduce. -->
  <div
    class="gaia-grid-cell"
    :class="{ frameless: props.frameless }"
    @click="emit('select', $event)"
  >
    <button
      v-if="props.expandable"
      type="button"
      class="expander"
      :title="props.expanded ? 'Collapse files' : 'Expand files'"
      @click.stop="emit('toggle', $event)"
    >
      <svg
        width="11"
        height="11"
        viewBox="0 0 12 12"
        fill="none"
        stroke="currentColor"
        stroke-width="1.7"
        stroke-linecap="round"
        stroke-linejoin="round"
      >
        <path d="M4.5 2.5 8 6l-3.5 3.5" />
      </svg>
    </button>

    <span v-if="props.fileIndent" class="file-indent" aria-hidden="true">
      <svg
        width="12"
        height="12"
        viewBox="0 0 14 14"
        fill="none"
        stroke="currentColor"
        stroke-width="1.3"
        stroke-linejoin="round"
      >
        <path d="M3.5 1.5h5l2.5 2.5v8.5h-7.5z" />
        <path d="M8.5 1.5V4H11" />
      </svg>
    </span>

    <svg
      v-if="busy"
      class="spinner"
      width="11"
      height="11"
      viewBox="0 0 12 12"
      fill="none"
      :stroke="spinnerColor"
      stroke-width="1.6"
      stroke-linecap="round"
    >
      <path d="M6 1.2A4.8 4.8 0 1 1 1.2 6" />
    </svg>

    <span v-if="props.state === 'failed'" class="failed" :title="props.errorTitle">!</span>

    <gaia-text
      class="value"
      size="dense"
      :tone="textTone"
      :weight="props.state === 'failed' ? 'semibold' : 'regular'"
      :style="{ fontStyle: busy ? 'italic' : 'normal', fontVariantNumeric: 'tabular-nums' }"
    >
      <slot>{{ props.state === 'blank' ? '—' : props.text }}</slot>
    </gaia-text>

    <gaia-overwrite-badge v-if="props.overwritten" tile :title="props.overwriteTitle" />

    <span v-if="props.approved" class="approved" title="Approved">
      <svg
        width="10"
        height="10"
        viewBox="0 0 10 10"
        fill="none"
        stroke="currentColor"
        stroke-width="1.9"
        stroke-linecap="round"
        stroke-linejoin="round"
      >
        <path d="M1.8 5.2 4 7.4 8.2 2.8" />
      </svg>
    </span>
  </div>
</template>

<style scoped>
.gaia-grid-cell {
  position: v-bind(position);
  left: v-bind(left);
  z-index: v-bind(layer);
  flex: none;
  width: v-bind(cellWidth);
  min-height: 44px;
  display: flex;
  align-items: center;
  gap: 7px;
  padding: 6px 12px;
  background: v-bind(background);
  border-right: 1px solid v-bind(border);
  box-shadow: v-bind(ring);
  overflow: hidden;
  cursor: pointer;
}

/* The host cell owns the box; this one keeps only its inner layout. */
.gaia-grid-cell.frameless {
  position: static;
  width: auto;
  min-height: 0;
  padding: 0;
  background: none;
  border-right: none;
  box-shadow: none;
}

.value {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.expander {
  width: 20px;
  height: 20px;
  flex: none;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: none;
  border-radius: 6px;
  background: none;
  color: v-bind(chevron);
  cursor: pointer;
}

.expander:hover {
  background: v-bind(hover);
  color: v-bind(ink);
}

.expander svg {
  transform: rotate(v-bind(chevronRotation));
  transition: transform 120ms;
}

/* Right-aligned inside a fixed gutter, so every child row's name starts on the
   same x whatever the glyph. */
.file-indent {
  width: 26px;
  flex: none;
  display: flex;
  justify-content: flex-end;
  padding-right: 2px;
  color: v-bind(blank);
}

.spinner {
  flex: none;
  animation: gaia-grid-spin 900ms linear infinite;
}

/* A tinted circle centring a "!". The font-size sizes that glyph inside a
   fixed 15px disc, so it is a box metric and stays off the type scale — the
   same call GaiaFilterChip's ✕ affordances make. The weight is a step. */
.failed {
  width: 15px;
  height: 15px;
  flex: none;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: v-bind(alertTint);
  color: v-bind(alert);
  font-size: 9.5px;
  font-weight: v-bind(bold);
}

/* Sits in the trailing corner rather than the content flow: approval is a note
   about the value, and it must not shorten the value's ellipsis. */
.approved {
  flex: none;
  margin-left: auto;
  display: inline-flex;
  color: v-bind(success);
}

@keyframes gaia-grid-spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
